import { useParams, useNavigate } from 'react-router-dom';
import { useEffect, useState, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { serviceProviderService } from '../../services';
import { ContractPackage, Document as ContractDocument } from '../../types';
import ContractPackageDetail from './ContractPackageDetail';
import { useNotification } from '../../context/NotificationContext';
import { refreshAll } from '../../services/refreshService';
import AnalysisCostDialog from '../common/AnalysisCostDialog';
import { useAnalysisCost } from '../../hooks/useAnalysisCost';
import { Box, Alert, Button } from '@mui/material';

// Types
interface ContractPackagePageParams extends Record<string, string | undefined> {
  dienstleisterId: string;
  vertragId: string;
}

interface ContractPackagePageProps {}

const ContractPackagePage: React.FC<ContractPackagePageProps> = () => {
  const { dienstleisterId, vertragId } = useParams<ContractPackagePageParams>();
  const navigate = useNavigate();
  const { showNotification } = useNotification();
  const { t } = useTranslation();
  
  // State
  const [loading, setLoading] = useState(true);
  const [contractPackage, setContractPackage] = useState<ContractPackage | null>(null);
  const [showCostDialog, setShowCostDialog] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Memoized validation
  const isValidParams = useMemo(() => 
    Boolean(dienstleisterId && vertragId), 
    [dienstleisterId, vertragId]
  );

  // Kostenschätzung für Analyse
  const costEstimation = useAnalysisCost({
    contractCount: 1,
    analysisType: 'single',
    isCritical: contractPackage?.isCritical ?? true
  });
  
  // Status-basierte Polling-Aktivierung - inkludiert QS-Status
  const shouldPoll = useMemo(() => {
    if (!contractPackage) return false;
    const status = contractPackage.status.toLowerCase();
    return status === 'pending' || status === 'analyzing' || status.startsWith('qs_');
  }, [contractPackage]);
  
  // Conversion utility
  const convertToContractPackage = useCallback((data: any): ContractPackage => {
    // console.log('Converting data to ContractPackage:', data);
    
    // Prüft, ob es sich bereits um ein valides ContractPackage handelt
    if ('isCritical' in data && 'documents' in data) {
      return data as ContractPackage;
    }
    
    // Konvertiert die Struktur oder fügt fehlende Felder hinzu
    return {
      _id: data._id,
      title: data.title,
      status: data.status,
      exitCode: data.exitCode,
      note: data.note || '',
      createdAt: data.createdAt || data.uploadDate,
      isCritical: data.isCritical, // Wert aus den Daten übernehmen
      documents: data.documents || [],
      analysisTypes: data.analysisTypes || undefined,
      analyseType: data.analyseType || undefined
    };
  }, []);

  // Data fetching
  const fetchContractData = useCallback(async () => {
    if (!isValidParams) {
      setError(t('providers:messages.loadServiceError'));
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      setError(null);
      const contractData = await serviceProviderService.getIndividualContractDetail(
        dienstleisterId!,
        vertragId!
      );
      const convertedData = convertToContractPackage(contractData);
      setContractPackage(convertedData);
      setError(null);
    } catch (error: any) {
      const errorMessage = error?.response?.status === 404
        ? t('providers:messages.serviceNotFound')
        : t('providers:messages.loadServiceError');
      setError(errorMessage);
      setContractPackage(null);
    } finally {
      setLoading(false);
    }
  }, [dienstleisterId, vertragId, isValidParams, convertToContractPackage, t]);

  // Effekt: immer bei Änderung der IDs neu laden
  useEffect(() => {
    setError(null);
    setContractPackage(null);
    setLoading(true);
    fetchContractData();
  }, [fetchContractData]);

  // Polling-Effekt
  useEffect(() => {
    if (!shouldPoll || !isValidParams) return;

    console.log('Starting polling for contract status:', contractPackage?.status);

    const pollData = async () => {
      try {
        const contractData = await serviceProviderService.getIndividualContractDetail(
          dienstleisterId!,
          vertragId!
        );
        const convertedData = convertToContractPackage(contractData);
                
        // Immer updaten, nicht nur bei Status-Änderung
        setContractPackage(convertedData);
      } catch (error) {
        console.error('Polling error:', error);
      }
    };

    const interval = setInterval(pollData, 2000); // 2 Sekunden
    return () => {
      console.log('Stopping polling for contract');
      clearInterval(interval);
    };
  }, [shouldPoll, isValidParams, dienstleisterId, vertragId, convertToContractPackage, contractPackage?.status]);

  // Event handlers
  const handleAddDocuments = useCallback(async (files: File[]) => {
    if (!isValidParams) return;
    
    try {
      const formData = new FormData();
      files.forEach(file => formData.append('files', file));
      
      const result = await serviceProviderService.addDocuments(
        dienstleisterId!, 
        vertragId!, 
        formData
      );
      
      if (result.status === 'success') {
        showNotification(result.message, 'success');
        await fetchContractData();
        refreshAll();
      } else {
        showNotification(result.message, 'error');
      }
    } catch (error) {
      console.error('Error adding documents:', error);
      showNotification(t('providers:messages.addDocumentsError'), 'error');
    }
  }, [dienstleisterId, vertragId, isValidParams, fetchContractData, showNotification]);

  const handleDeleteContractPackage = useCallback(async () => {
    if (!isValidParams) return;
    
    try {
      const result = await serviceProviderService.deleteIndividualContract(
        dienstleisterId!, 
        vertragId!
      );
      
      if (result.status === 'success') {
        navigate(`/dienstleister/${dienstleisterId}`);
        showNotification(result.message, 'success');

      } else {
        showNotification(result.message, 'error');
      }
    } catch (error) {
      console.error('Error deleting:', error);
      showNotification(t('providers:messages.deleteServiceError'), 'error');
    }
  }, [dienstleisterId, vertragId, isValidParams, navigate, showNotification]);

  // Optimistic Update für einzelnen Contract
  const performOptimisticUpdate = useCallback(() => {
    setContractPackage(prevContract => {
      if (!prevContract) return prevContract;
      return { ...prevContract, status: 'pending' as const };
    });
    
    // Nach 1,5 Sekunden die echten Daten vom Server abrufen
    setTimeout(async () => {
      try {
        await fetchContractData();
        refreshAll();
      } catch (error) {
        console.error('Error refreshing data:', error);
      }
    }, 1500);
  }, [fetchContractData]);

  const handleStartAnalysisConfirmed = useCallback(async () => {
    if (!isValidParams) return;
    
    // Sofortiges UI Update
    performOptimisticUpdate();
    
    try {
      const result = await serviceProviderService.analyzeIndividualContract(
        dienstleisterId!, 
        vertragId!
      );
      
      if (result.status === 'success') {
        showNotification(t('providers:messages.analyzeSuccess'), 'success');
      } else {
        showNotification(result.message, 'error');
        // Bei Fehler: Daten sofort neu laden
        await fetchContractData();
      }
    } catch (error) {
      console.error('Error during analysis:', error);
      showNotification(t('providers:messages.analysisError'), 'error');
      // Bei Fehler: Daten sofort neu laden
      await fetchContractData();
    }
  }, [dienstleisterId, vertragId, isValidParams, showNotification, performOptimisticUpdate, fetchContractData, t]);

  const handleStartAnalysis = useCallback(() => {
    setShowCostDialog(true);
  }, []);

  const handleCancelAnalysis = useCallback(async () => {
    if (!isValidParams) return;
    
    // Prüfe ob QS-Status vorliegt - diese können nicht abgebrochen werden
    if (contractPackage?.status?.startsWith('qs_')) {
      showNotification(t('providers:messages.qsCannotCancelSingle'), 'warning');
      return;
    }
    
    try {
      const result = await serviceProviderService.cancelIndividualContractAnalysis(
        dienstleisterId!, 
        vertragId!
      );
      
      if (result.status === 'success') {
        showNotification(result.message, 'success');
        // Sofortiges Update nach dem Abbrechen
        await fetchContractData();
        refreshAll();
      } else {
        showNotification(result.message, 'error');
      }
    } catch (error) {
      console.error('Error cancelling analysis:', error);
      showNotification(t('providers:messages.analysisCancelError'), 'error');
    }
  }, [dienstleisterId, vertragId, isValidParams, showNotification, fetchContractData, contractPackage]);

  const handleEditTitle = useCallback(async (newTitle?: string) => {
    if (!isValidParams || !newTitle) return;
    
    try {
      const result = await serviceProviderService.editIndividualContractTitle(
        dienstleisterId!, 
        vertragId!, 
        newTitle
      );
      
      if (result.status === 'success') {
        showNotification(result.message, 'success');
        await fetchContractData();
      } else {
        showNotification(result.message, 'error');
      }
    } catch (error) {
      console.error('Error editing title:', error);
      showNotification(t('providers:messages.editTitleError'), 'error');
    }
  }, [dienstleisterId, vertragId, isValidParams, fetchContractData, showNotification]);
  
  const handleDownloadContractPackage = useCallback(async () => {
    if (!isValidParams) return;
    
    try {
      await serviceProviderService.downloadIndividualContract(dienstleisterId!, vertragId!);
    } catch (error) {
      console.error('Error downloading:', error);
      showNotification(t('providers:messages.downloadError'), 'error');
    }
  }, [dienstleisterId, vertragId, isValidParams, showNotification]);

  const handleDownloadDocument = useCallback(async (document: ContractDocument, documentId: string) => {
    if (!isValidParams) return;
    
    try {
      const blob = await serviceProviderService.downloadContractDocument(
        dienstleisterId!, 
        vertragId!, 
        documentId
      );
      
      if (blob.size === 0) {
        throw new Error('Die heruntergeladene Datei ist leer');
      }
      
      const url = window.URL.createObjectURL(blob);
      const a = window.document.createElement('a');
      a.href = url;
      a.download = document.title || `document_${documentId}`;
      a.style.display = 'none';
      window.document.body.appendChild(a);
      a.click();
      
      // Cleanup nach kurzer Verzögerung
      setTimeout(() => {
        window.URL.revokeObjectURL(url);
        window.document.body.removeChild(a);
      }, 100);
      
      showNotification(t('providers:messages.documentDownloadSuccess'), 'success');
    } catch (error) {
      console.error('Error downloading document:', error);
      showNotification(t('providers:messages.documentDownloadError'), 'error');
    }
  }, [dienstleisterId, vertragId, isValidParams, showNotification]);

  const handleDeleteDocuments = useCallback(async (documentIds: string[]) => {
    if (!isValidParams) return;
    
    try {
      const result = await serviceProviderService.deleteMultipleDocuments(
        dienstleisterId!, 
        vertragId!, 
        documentIds
      );
      
      if (result.status === 'success') {
        showNotification(result.message, 'success');
        await fetchContractData();
        refreshAll();
      } else {
        showNotification(result.message, 'error');
      }
    } catch (error) {
      console.error('Error deleting documents:', error);
      showNotification(t('providers:messages.deleteDocumentsError'), 'error');
    }
  }, [dienstleisterId, vertragId, isValidParams, fetchContractData, showNotification]);

  const handleDeleteSingleDocument = useCallback(async (documentId: string) => {
    if (!isValidParams) return;
    
    try {
      const result = await serviceProviderService.deleteDocument(
        dienstleisterId!, 
        vertragId!, 
        documentId
      );
      
      if (result.status === 'success') {
        showNotification(result.message, 'success');
        await fetchContractData();
        refreshAll();
      } else {
        showNotification(result.message, 'error');
      }
    } catch (error) {
      console.error('Error deleting document:', error);
      showNotification(t('providers:messages.deleteDocumentError'), 'error');
    }
  }, [dienstleisterId, vertragId, isValidParams, fetchContractData, showNotification]);

  const handleSaveNote = useCallback(async (note: string) => {
    if (!isValidParams) return;
    
    try {
      const result = await serviceProviderService.saveIndividualContractNote(
        dienstleisterId!, 
        vertragId!, 
        note
      );
      
      if (result.status === 'success') {
        showNotification(result.message, 'success');
        await fetchContractData();
      } else {
        showNotification(result.message, 'error');
      }
    } catch (error) {
      console.error('Error saving note:', error);
      showNotification(t('providers:messages.saveNoteError'), 'error');
    }
  }, [dienstleisterId, vertragId, isValidParams, fetchContractData, showNotification]);

  const handleDownloadResults = useCallback(async () => {
    if (!isValidParams) return;
    
    try {
      await serviceProviderService.downloadContractResultsExcel(dienstleisterId!, vertragId!);
      showNotification(t('providers:messages.downloadResultsSuccess'), 'success');
    } catch (error) {
      console.error('Error downloading analysis results:', error);
      showNotification(t('providers:messages.downloadResultsError'), 'error');
    }
  }, [dienstleisterId, vertragId, isValidParams, showNotification]);

  const handleUpdateCriticality = useCallback(async (isCritical: boolean) => {
    if (!isValidParams) return;

    try {
      const result = await serviceProviderService.updateIndividualContractCriticality(
        dienstleisterId!,
        vertragId!,
        isCritical
      );

      if (result.status === 'success') {
        showNotification(t('providers:messages.criticalityUpdateSuccess'), 'success');
        await fetchContractData(); // Refresh data to show the change
        refreshAll();
      } else {
        showNotification(result.message || t('providers:messages.genericError'), 'error');
      }
    } catch (error) {
      console.error('Error updating criticality:', error);
      showNotification(t('providers:messages.criticalityUpdateError'), 'error');
    }
  }, [dienstleisterId, vertragId, isValidParams, showNotification, fetchContractData, t]);

  // Memoized computed values
  const documents = useMemo(() => contractPackage?.documents || [], [contractPackage]);
  const isAnalysisDisabled = useMemo(() => contractPackage?.status === 'complete', [contractPackage]);

  // Loading state
  if (loading && !error) {
    return <Box sx={{ p: 3 }}>{t('providers:messages.loadingData')}</Box>;
  }

  // Error state
  if (error && !contractPackage) {
    return (
      <Alert 
        severity="error" 
        sx={{ m: 3 }}
        action={
          <Button color="inherit" size="small" onClick={() => navigate('/dienstleister')} data-testid="contract-package-page-error-back-button">
            {t('providers:messages.backToOverview')}
          </Button>
        }
        data-testid="contract-package-page-error-alert"
      >
        {t('providers:messages.serviceNotFound')}
      </Alert>
    );
  }

  

  // Not found state
  if (!contractPackage) {
    return (
      <div style={{ padding: '20px', textAlign: 'center' }}>
        <div>{t('providers:messages.serviceNotFound')}</div>
      </div>
    );
  }

  return (
    <>
    <ContractPackageDetail
      dienstleisterId={dienstleisterId || ''}
      contractPackageId={vertragId}
      title={contractPackage.title}
      status={contractPackage.status}
      exitCode={(contractPackage as any).exitCode}
      note={contractPackage.note}
      documents={documents}
      analyseType={(contractPackage as any).analyseType}
      onAddDocuments={handleAddDocuments}
      onDeleteContractPackage={handleDeleteContractPackage}
      onStartAnalysis={handleStartAnalysisConfirmed}
      onCancelAnalysis={handleCancelAnalysis}
      onEditTitle={handleEditTitle}
      handleDeleteDocuments={handleDeleteDocuments}
      handleDeleteSingleDocument={handleDeleteSingleDocument}
      handleDownloadContractPackage={handleDownloadContractPackage}
      handleDownloadDocument={handleDownloadDocument}
      handleDownloadResults={handleDownloadResults}
      isAnalysisDisabled={isAnalysisDisabled}
      handleSaveNote={handleSaveNote}
      onUpdateCriticality={handleUpdateCriticality}
      isCritical={contractPackage.isCritical}
      analysisTypes={(contractPackage as any).analysisTypes}
    />


    </>
  );
};

export default ContractPackagePage; 